import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {toast} from 'react-toastify'
import Select, {type StylesConfig} from 'react-select'
import {useAuth, isPlatformSuperAdmin} from '../../auth'
import {getAdminPermissionCatalog} from '../core/accessRequests'
import {getCompanyGroupAdminList, getManagementUsers} from '../core/_requests'
import type {ManagementUser} from '../core/_models'
import {rolePermissionManagementGates} from '../core/adminPermissions'
import {
  createCustomRole,
  getRolesList,
  getRolePermissionsList,
  replaceRolePermissionsForRole,
  updateRole,
  type RoleRecord,
  type RolePermissionRecord,
} from '../core/rolePermissionRequests'
import {
  humanizePermissionCode,
  resolveCatalogEntry,
  type PermissionCatalogEntry,
} from '../core/permissionGrouping'
import {
  assignProductionPermissionPlacement,
  resolveAuthoritativePermissionPlacement,
  buildPermissionGroupingDebugSnapshot,
  dedupeCatalogByPermissionCode,
  PRODUCTION_MODULE_ORDER,
  productionModuleOrderIndex,
  type PermissionGroupingDebugSnapshot,
} from '../core/productionPermissionGrouping'
import {
  buildProductionMenuCoverageReport,
  type MenuCoverageDiagnostics,
} from '../core/productionMenuCoverageDiagnostics'
import {warnCargoOperationsMisplacementDev} from '../core/productionCargoOperationsAudit'
import {
  buildPermissionMatrixRows,
  normalizeCode,
  STD_LABELS,
  STD_ORDER,
  type MatrixRow,
  type StdAction,
} from '../core/rolePermissionMatrix'
import {
  buildPermissionIdByCode,
  buildSelectedPermissionCountByRole,
  collectReplacementPermissionPayload,
  filterGrantablePermissionCodes,
  collectSelectedPermissionCodesForRole,
} from '../core/rolePermissionEffectiveState'
import {
  buildGrantableByCodeMap,
  grantDeniedReasonFor,
  isGrantablePermissionCode,
} from '../core/rolePermissionGrantability'
import {canonicalizeRolePermissionCode} from '../core/rolePermissionCanonicalization'
import './adminAccessRoleModals.scss'
import {
  canManageRolePermissions,
  resolveActorCompanyGroupId,
  resolveActorCompanyAdminId,
  rolePermissionManageBlockedReason,
  type RolePermissionActorContext,
} from '../core/rolePermissionHierarchy'
import {OperatorSubRolesManagerPanel} from './OperatorSubRolesManagerPanel'
import {CrewRankPermissionsManagerPanel} from './CrewRankPermissionsManagerPanel'

/** Strip leading "Can " from catalog text for compact checkbox labels. */
function shortActionLabel(text: string): string {
  return (text || '').replace(/^\s*can\s+/i, '').trim() || text
}

const TABLE_HEADER_BG = '#152a45'
const TABLE_BORDER_OUTER = '#b8c0cc'
const TABLE_CELL_BORDER = '#d8dde6'
const ROW_ALT = '#f7f9fc'
const MODULE_CELL_BG = '#e9f0f7'
const CHECK_ACCENT = '#0bb7a0'

export type RolePermissionsModalProps = {
  open: boolean
  onClose: () => void
  showRoleManagementTab?: boolean
  showRolesTab?: boolean
  showOperatorSubRolesTab?: boolean
  showCrewRankPermissionsTab?: boolean
}

type AccessTab = 'role-management' | 'roles' | 'operator-sub-roles' | 'crew-rank-permissions'

type RoleCrudMode = 'create' | 'edit' | 'view' | 'duplicate'

type RoleSelectOption = {
  value: number
  label: string
}

const roleSelectStyles: StylesConfig<RoleSelectOption, false> = {
  container: (base) => ({...base, minWidth: 280, maxWidth: 420, flex: '0 1 420px'}),
  control: (base) => ({...base, minHeight: 31, height: 31}),
  valueContainer: (base) => ({...base, padding: '0 8px'}),
  indicatorsContainer: (base) => ({...base, height: 31}),
  menuPortal: (base) => ({...base, zIndex: 9999}),
  menu: (base) => ({...base, zIndex: 9999, maxHeight: 300}),
  menuList: (base) => ({...base, maxHeight: 300, overflowY: 'auto'}),
}

function isSuperAdminRoleRecord(r: RoleRecord): boolean {
  const t = (r.roleType || '').toUpperCase()
  if (t === 'SUPER_ADMIN') return true
  if (r.assignable === false) return true
  return false
}

function canEditRolePermissions(
  role: RoleRecord | null,
  actor: RolePermissionActorContext
): boolean {
  return canManageRolePermissions(role, actor)
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false
  for (const x of Array.from(a)) {
    if (!b.has(x)) return false
  }
  return true
}

function allCodesOnRow(row: MatrixRow): string[] {
  const s = STD_ORDER.map((k) => row.standard[k]).filter(Boolean) as string[]
  return s.concat(row.extraChecks.map((x) => x.code))
}

function allCodesForModule(rows: MatrixRow[], moduleName: string): string[] {
  const set = new Set<string>()
  for (const r of rows) {
    if (r.moduleName !== moduleName) continue
    for (const c of allCodesOnRow(r)) set.add(normalizeCode(c))
  }
  return Array.from(set)
}

/** All permission codes represented in the matrix (equals full catalog codes). */
function allMatrixCodes(rows: MatrixRow[]): string[] {
  const set = new Set<string>()
  for (const r of rows) {
    for (const c of allCodesOnRow(r)) set.add(normalizeCode(c))
  }
  return Array.from(set)
}

function codesVisibleInFilteredRows(filtered: MatrixRow[]): string[] {
  const set = new Set<string>()
  for (const r of filtered) {
    for (const c of allCodesOnRow(r)) set.add(normalizeCode(c))
  }
  return Array.from(set)
}

function rowMatchesSearch(row: MatrixRow, q: string): boolean {
  if (!q.trim()) return true
  const u = q.trim().toUpperCase()
  const parts: string[] = [
    row.moduleName,
    row.menuName,
    row.subMenuName,
    row.rowKey,
    ...STD_ORDER.map((k) => row.standard[k]).filter(Boolean) as string[],
    ...STD_ORDER.map((k) => row.standardHints[k]).filter(Boolean) as string[],
    ...row.extraChecks.flatMap((x) => [x.code, x.label, x.catalogDetail || '']),
  ]
  return parts.some((p) => p.toUpperCase().includes(u))
}

type ModuleTriState = 'all' | 'some' | 'none'

function moduleTriState(
  moduleName: string,
  allRows: MatrixRow[],
  draft: Set<string>
): ModuleTriState {
  const needed = allCodesForModule(allRows, moduleName)
  if (needed.length === 0) return 'none'
  let on = 0
  for (const c of needed) {
    if (draft.has(c)) on++
  }
  if (on === 0) return 'none'
  if (on === needed.length) return 'all'
  return 'some'
}

export const RolePermissionsModal: React.FC<RolePermissionsModalProps> = ({
  open,
  onClose,
  showRoleManagementTab: showRoleManagementTabProp,
  showRolesTab: showRolesTabProp,
  showOperatorSubRolesTab: showOperatorSubRolesTabProp,
  showCrewRankPermissionsTab: showCrewRankPermissionsTabProp,
}) => {
  const {currentUser, auth, hasPermission, hasAnyPermission, isSuperAdmin} = useAuth()
  const isSuperUser = isPlatformSuperAdmin(currentUser, auth)
  const actorCgaId = useMemo(() => resolveActorCompanyGroupId(currentUser as any), [currentUser])
  const effectiveBehaviorType = useMemo(() => {
    const u = currentUser as Record<string, unknown> | null | undefined
    const role = u?.role as {roleType?: string; baseRoleType?: string; effectiveBehaviorType?: string} | undefined
    return (
      role?.effectiveBehaviorType ||
      role?.baseRoleType ||
      role?.roleType ||
      (u?.roleType as string | undefined) ||
      null
    )
  }, [currentUser])
  const gates = useMemo(
    () => rolePermissionManagementGates(hasPermission, hasAnyPermission, isSuperAdmin, effectiveBehaviorType),
    [hasPermission, hasAnyPermission, isSuperAdmin, effectiveBehaviorType]
  )
  const actorPermissionContext = useMemo<RolePermissionActorContext>(
    () => ({
      isSuperAdmin: isSuperUser,
      actorRoleId: Number((currentUser as any)?.role?.id ?? (currentUser as any)?.roleId ?? 0) || null,
      actorCompanyGroupId: actorCgaId,
      actorCompanyAdminId: resolveActorCompanyAdminId(currentUser as any),
      actorEffectiveBehavior: effectiveBehaviorType,
      hasUpdateRolePermission: gates.canSaveRolePermissions,
    }),
    [actorCgaId, currentUser, effectiveBehaviorType, gates.canSaveRolePermissions, isSuperUser]
  )
  const showRoleManagementTab = showRoleManagementTabProp ?? gates.showRoleManagementTab
  const showRolesTab = showRolesTabProp ?? gates.showRolesTab
  const showOperatorSubRolesTab = showOperatorSubRolesTabProp ?? gates.showOperatorSubRolesTab
  const showCrewRankPermissionsTab = showCrewRankPermissionsTabProp ?? gates.showCrewRankPermissionsTab
  const availableTabs = useMemo<AccessTab[]>(() => {
    const tabs: AccessTab[] = []
    if (showRoleManagementTab) tabs.push('role-management')
    if (showRolesTab) tabs.push('roles')
    if (showOperatorSubRolesTab) tabs.push('operator-sub-roles')
    if (showCrewRankPermissionsTab) tabs.push('crew-rank-permissions')
    return tabs
  }, [showCrewRankPermissionsTab, showOperatorSubRolesTab, showRoleManagementTab, showRolesTab])
  const [activeTab, setActiveTab] = useState<AccessTab>('role-management')

  const [bootLoading, setBootLoading] = useState(false)
  const [rolesAccessDenied, setRolesAccessDenied] = useState(false)
  const [catalogAccessDenied, setCatalogAccessDenied] = useState(false)
  const [rolePermissionsAccessDenied, setRolePermissionsAccessDenied] = useState(false)
  const [rolesList, setRolesList] = useState<RoleRecord[]>([])
  const [managementUsers, setManagementUsers] = useState<ManagementUser[]>([])
  const [permissionCatalog, setPermissionCatalog] = useState<PermissionCatalogEntry[]>([])
  const [rolePermissionsList, setRolePermissionsList] = useState<RolePermissionRecord[]>([])

  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)
  const [permSearch, setPermSearch] = useState('')

  const [draftCodes, setDraftCodes] = useState<Set<string>>(new Set())
  const [savedCodes, setSavedCodes] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)
  const [roleCrudOpen, setRoleCrudOpen] = useState(false)
  const [roleCrudMode, setRoleCrudMode] = useState<RoleCrudMode>('create')
  const [roleCrudTarget, setRoleCrudTarget] = useState<RoleRecord | null>(null)
  const [roleCrudBusy, setRoleCrudBusy] = useState(false)
  const [roleNameInput, setRoleNameInput] = useState('')
  const [roleCodeInput, setRoleCodeInput] = useState('')
  const [roleDescriptionInput, setRoleDescriptionInput] = useState('')
  const [roleBaseTypeInput, setRoleBaseTypeInput] = useState('COMPANY_ADMIN')
  const [roleActiveInput, setRoleActiveInput] = useState(true)
  const [roleCompanyGroupAdminId, setRoleCompanyGroupAdminId] = useState<number | null>(null)
  const [roleCompanyGroupOptions, setRoleCompanyGroupOptions] = useState<{id: number; label: string}[]>([])
  const [roleCompanyGroupLoadError, setRoleCompanyGroupLoadError] = useState<string | null>(null)

  const dedupedPermissionCatalog = useMemo(
    () => dedupeCatalogByPermissionCode(permissionCatalog).deduped,
    [permissionCatalog]
  )

  const matrixRows = useMemo(
    () => buildPermissionMatrixRows(dedupedPermissionCatalog),
    [dedupedPermissionCatalog]
  )
  const filteredRows = useMemo(
    () => matrixRows.filter((r) => rowMatchesSearch(r, permSearch)),
    [matrixRows, permSearch]
  )
  const moduleCheckRefs = useRef<Map<string, HTMLInputElement | null>>(new Map())
  /** Latest raw API catalog — `window.__dumpPermissionGroupingDebug()` reads this live in development */
  const permissionCatalogRef = useRef(permissionCatalog)
  permissionCatalogRef.current = permissionCatalog

  const editableRoles = useMemo(
    () =>
      rolesList.filter((role) => {
        const behavior = String(
          role.effectiveBehaviorType || role.baseRoleType || role.roleType || ''
        ).toUpperCase()
        return behavior !== 'CREW' && canManageRolePermissions(role, actorPermissionContext)
      }),
    [actorPermissionContext, rolesList]
  )
  // The API already applies the actor's global + company-group visibility scope.
  // Keep selector visibility separate from editability so same-rank roles can be viewed read-only.
  const roleSelectorRoles = useMemo(
    () => rolesList.filter((role) => !isSuperAdminRoleRecord(role)),
    [rolesList]
  )
  const selectedRole = useMemo(
    () => roleSelectorRoles.find((r) => r.id === selectedRoleId) ?? null,
    [roleSelectorRoles, selectedRoleId]
  )
  const roleSelectOptions = useMemo<RoleSelectOption[]>(
    () =>
      roleSelectorRoles.map((r) => ({
        value: r.id,
        label: `${(r.displayName || r.roleType || `Role ${r.id}`).replace(/_/g, ' ')}${
          r.code ? ` · ${r.code}` : ''
        } (#${r.id})`,
      })),
    [roleSelectorRoles]
  )
  const permissionCountByRoleId = useMemo(() => {
    return buildSelectedPermissionCountByRole(rolePermissionsList)
  }, [rolePermissionsList])
  const usersCountByRoleId = useMemo(() => {
    const map = new Map<number, number>()
    managementUsers.forEach((u) => {
      const rid = Number(u.roleId ?? 0)
      if (!Number.isFinite(rid) || rid <= 0) return
      map.set(rid, (map.get(rid) ?? 0) + 1)
    })
    return map
  }, [managementUsers])

  const readOnlyPermissions =
    !gates.canSaveRolePermissions ||
    !canEditRolePermissions(selectedRole, actorPermissionContext)
  const dirtyPermissions = useMemo(() => !setsEqual(draftCodes, savedCodes), [draftCodes, savedCodes])
  const isProtectedRoleKind =
    !!selectedRole && (selectedRole.systemRole === true || isSuperAdminRoleRecord(selectedRole))
  const readOnlyNotice =
    !selectedRole || !readOnlyPermissions
      ? null
      : rolePermissionManageBlockedReason(selectedRole, actorPermissionContext)

  const searchActive = permSearch.trim().length > 0
  const permissionIdByCode = useMemo(
    () => buildPermissionIdByCode(rolePermissionsList, permissionCatalog),
    [permissionCatalog, rolePermissionsList]
  )
  const grantableByCode = useMemo(
    () => buildGrantableByCodeMap(dedupedPermissionCatalog, isSuperUser),
    [dedupedPermissionCatalog, isSuperUser]
  )
  const canGrantPermission = useCallback(
    (code: string) => isGrantablePermissionCode(code, grantableByCode, isSuperUser),
    [grantableByCode, isSuperUser]
  )
  const grantableCodesFor = useCallback(
    (codes: Iterable<string>) =>
      Array.from(codes).filter((code) => canGrantPermission(code)).map((code) => normalizeCode(code)),
    [canGrantPermission]
  )
  const staleDisabledCodes = useMemo(
    () => Array.from(savedCodes).filter((code) => !canGrantPermission(code)),
    [canGrantPermission, savedCodes]
  )
  const moduleTriStateForGrantable = useCallback(
    (moduleName: string): ModuleTriState => {
      const needed = grantableCodesFor(allCodesForModule(matrixRows, moduleName))
      if (needed.length === 0) return 'none'
      let on = 0
      for (const code of needed) {
        if (draftCodes.has(code)) on += 1
      }
      if (on === 0) return 'none'
      if (on === needed.length) return 'all'
      return 'some'
    },
    [draftCodes, grantableCodesFor, matrixRows]
  )

  useEffect(() => {
    setSelectedRoleId((prev) => {
      if (prev != null && roleSelectorRoles.some((role) => role.id === prev)) return prev
      return editableRoles[0]?.id ?? roleSelectorRoles[0]?.id ?? null
    })
  }, [editableRoles, roleSelectorRoles])

  useEffect(() => {
    for (const [mod, el] of Array.from(moduleCheckRefs.current.entries())) {
      if (!el) continue
      const st = moduleTriStateForGrantable(mod)
      el.indeterminate = st === 'some'
      el.checked = st === 'all'
    }
  }, [moduleTriStateForGrantable])

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return
    type WinDbg = Window & {
      __dumpPermissionGroupingDebug?: () => PermissionGroupingDebugSnapshot
      __dumpPermissionGroupingCoverage?: () => MenuCoverageDiagnostics
    }
    const dump = () => buildPermissionGroupingDebugSnapshot(permissionCatalogRef.current)
    const dumpCoverage = () => buildProductionMenuCoverageReport(permissionCatalogRef.current)
    const w = window as WinDbg
    w.__dumpPermissionGroupingDebug = dump
    w.__dumpPermissionGroupingCoverage = dumpCoverage
    return () => {
      delete w.__dumpPermissionGroupingDebug
      delete w.__dumpPermissionGroupingCoverage
    }
  }, [])

  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || !open || permissionCatalog.length === 0) return
    warnCargoOperationsMisplacementDev(permissionCatalog)
    const snap = buildPermissionGroupingDebugSnapshot(permissionCatalog)
    const v = snap.validation
    /* eslint-disable no-console */
    console.info('[RolePermissionsModal] validateProductionPermissionGrouping (full)', v)
    console.info('[RolePermissionsModal] grouping preview — first 50 flat rows:', snap.groupedRows.slice(0, 50))
    console.table(snap.groupedRows.slice(0, 50))
    console.info('[RolePermissionsModal] JSON.download tip: JSON.stringify(window.__dumpPermissionGroupingDebug?.(), null, 2)')
    const coverage = buildProductionMenuCoverageReport(permissionCatalog)
    console.info('[RolePermissionsModal] production menu coverage summary', {
      sidebarLeafExpectationCount: coverage.sidebarLeafExpectationCount,
      sidebarLeavesWithMappedPermissionsCount: coverage.sidebarLeavesWithMappedPermissionsCount,
      sidebarLeavesWithNoMappedPermissionsCount: coverage.sidebarLeavesWithNoMappedPermissionsCount,
      unmappedPermissionCountApprox: coverage.unmappedPermissionCountApprox,
      unexpectedPlacementKeyCount: coverage.unexpectedPlacementKeys.length,
      leavesWithoutMappedPermissionsPreview: coverage.leaves
        .filter((l) => !l.synthetic && l.mappedCount === 0)
        .slice(0, 30)
        .map((l) => ({module: l.module, segments: l.segments, route: l.route})),
    })
    if (v.duplicateCatalogCodes.length > 0) {
      console.warn(
        '[RolePermissionsModal] duplicate permission_code rows from API:',
        v.duplicateCatalogCodes.length,
        v.duplicateCatalogCodes
      )
    }
    if (v.missingCodes.length > 0 || v.duplicateRenderedCodes.length > 0) {
      console.warn('[RolePermissionsModal] grouped-model invariant FAILED', {
        missingCodes: v.missingCodes,
        duplicateRenderedCodes: v.duplicateRenderedCodes,
      })
    }
    /* eslint-enable no-console */
  }, [open, permissionCatalog])

  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || !open || permissionCatalog.length === 0) return
    const counts: Record<string, number> = {}
    for (const k of PRODUCTION_MODULE_ORDER) counts[k] = 0
    counts.Other = 0
    for (const raw of dedupedPermissionCatalog) {
      const pl = assignProductionPermissionPlacement(raw, dedupedPermissionCatalog)
      const m = pl.moduleLabel
      if (counts[m] !== undefined) counts[m]++
      else counts.Other++
    }
    // eslint-disable-next-line no-console
    console.info(
      '[RolePermissionsModal] catalog counts by production module (deduped)',
      counts,
      'unique',
      dedupedPermissionCatalog.length,
      'raw API rows',
      permissionCatalog.length
    )
  }, [open, permissionCatalog, dedupedPermissionCatalog])

  const rebuildDraftFromRps = useCallback(
    (roleId: number | null, rps: RolePermissionRecord[]) => {
      const next = collectSelectedPermissionCodesForRole(roleId, rps)
      setDraftCodes(next)
      setSavedCodes(new Set(next))
    },
    []
  )

  useEffect(() => {
    if (availableTabs.length === 0) return
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0])
    }
  }, [activeTab, availableTabs])

  useEffect(() => {
    if (!open) {
      setRolesList([])
      setPermissionCatalog([])
      setRolePermissionsList([])
      setManagementUsers([])
      setSelectedRoleId(null)
      setPermSearch('')
      setDraftCodes(new Set())
      setSavedCodes(new Set())
      setRolesAccessDenied(false)
      setCatalogAccessDenied(false)
      setRolePermissionsAccessDenied(false)
      if (availableTabs.length > 0) setActiveTab(availableTabs[0])
      return
    }

    let cancelled = false
    ;(async () => {
      setBootLoading(true)
      setRolesAccessDenied(false)
      setCatalogAccessDenied(false)
      setRolePermissionsAccessDenied(false)
      const cgaFilter = isSuperUser ? undefined : actorCgaId

      const rolesTask = gates.canReadRole
        ? getRolesList(false, cgaFilter ?? null)
        : Promise.reject(new Error('Access denied'))
      const catalogTask = gates.canReadPermission
        ? getAdminPermissionCatalog()
        : Promise.reject(new Error('Access denied'))
      const rolePermissionsTask = gates.canReadRolePermission
        ? getRolePermissionsList()
        : Promise.reject(new Error('Access denied'))

      const [rolesResult, catalogResult, rpsResult, usersResult] = await Promise.allSettled([
        rolesTask,
        catalogTask,
        rolePermissionsTask,
        getManagementUsers().catch(() => [] as ManagementUser[]),
      ])

      if (cancelled) return

      if (rolesResult.status === 'fulfilled') {
        const visible = isSuperUser
          ? rolesResult.value
          : rolesResult.value.filter((r) => !isSuperAdminRoleRecord(r))
        setRolesList(visible)
      } else {
        setRolesList([])
        setRolesAccessDenied(gates.canReadRole)
        if (gates.canReadRole) {
          toast.error(rolesResult.reason?.message || 'Failed to load roles')
        }
      }

      if (catalogResult.status === 'fulfilled') {
        setPermissionCatalog(catalogResult.value)
      } else {
        setPermissionCatalog([])
        setCatalogAccessDenied(gates.canReadPermission)
        if (gates.canReadPermission) {
          toast.error(catalogResult.reason?.message || 'Failed to load permission catalog')
        }
      }

      if (rpsResult.status === 'fulfilled') {
        setRolePermissionsList(rpsResult.value)
      } else {
        setRolePermissionsList([])
        setRolePermissionsAccessDenied(gates.canReadRolePermission)
        if (gates.canReadRolePermission) {
          toast.error(rpsResult.reason?.message || 'Failed to load role permissions')
        }
      }

      setManagementUsers(usersResult.status === 'fulfilled' ? usersResult.value || [] : [])
      setBootLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [
    open,
    isSuperUser,
    actorCgaId,
    availableTabs,
    gates.canReadRole,
    gates.canReadPermission,
    gates.canReadRolePermission,
  ])

  useEffect(() => {
    rebuildDraftFromRps(selectedRoleId, rolePermissionsList)
  }, [selectedRoleId, rolePermissionsList, rebuildDraftFromRps])

  const setDraftMany = useCallback((codes: string[], selected: boolean) => {
    setDraftCodes((prev) => {
      const next = new Set(prev)
      for (const c of codes) {
        const k = canonicalizeRolePermissionCode(c)
        if (selected && !isGrantablePermissionCode(c, grantableByCode, isSuperUser)) continue
        if (selected) next.add(k)
        else next.delete(k)
      }
      return next
    })
  }, [grantableByCode, isSuperUser])

  const toggleDraftPermission = (code: string) => {
    if (readOnlyPermissions || !canGrantPermission(code)) return
    const key = canonicalizeRolePermissionCode(code)
    setDraftCodes((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleCancel = () => {
    setDraftCodes(new Set(savedCodes))
    onClose()
  }

  const handleSavePermissions = async () => {
    if (selectedRoleId == null || readOnlyPermissions) return
    const {grantableCodes, removedStaleCodes} = filterGrantablePermissionCodes(
      draftCodes,
      grantableByCode,
      isSuperUser
    )
    const grantableDraft = new Set(grantableCodes)
    setSaving(true)
    try {
      const response = await replaceRolePermissionsForRole(
        selectedRoleId,
        collectReplacementPermissionPayload(grantableDraft, permissionIdByCode, grantableByCode, isSuperUser)
      )
      const rps = await getRolePermissionsList()
      setRolePermissionsList(rps)
      rebuildDraftFromRps(selectedRoleId, rps)
      const droppedCount =
        (response?.staleRemovedCount ?? 0) +
        (response?.droppedParentUnavailableCount ?? removedStaleCodes.length)
      if (droppedCount > 0) {
        toast.warn(
          droppedCount === 1
            ? '1 permission removed because it is no longer available in your parent role.'
            : `${droppedCount} permissions removed because they are no longer available in your parent role.`
        )
      }
      toast.success('Permissions saved.')
      window.dispatchEvent(new CustomEvent('rbac-permissions-updated', {detail: {roleId: selectedRoleId}}))
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save permissions')
    } finally {
      setSaving(false)
    }
  }

  const selectAllCatalog = () => {
    if (readOnlyPermissions) return
    setDraftMany(allMatrixCodes(matrixRows), true)
  }

  const clearAllCatalog = () => {
    if (readOnlyPermissions) return
    setDraftCodes(new Set())
  }

  const selectVisibleOnly = () => {
    if (readOnlyPermissions) return
    setDraftMany(codesVisibleInFilteredRows(filteredRows), true)
  }

  const clearVisibleOnly = () => {
    if (readOnlyPermissions) return
    setDraftMany(codesVisibleInFilteredRows(filteredRows), false)
  }

  const toggleModuleAll = (moduleName: string, check: boolean) => {
    if (readOnlyPermissions) return
    setDraftMany(allCodesForModule(matrixRows, moduleName), check)
  }

  const toggleRowAll = (row: MatrixRow, check: boolean) => {
    if (readOnlyPermissions) return
    setDraftMany(allCodesOnRow(row), check)
  }

  useEffect(() => {
    if (!roleCrudOpen) {
      setRoleCompanyGroupOptions([])
      setRoleCompanyGroupLoadError(null)
      return
    }
    let cancelled = false
    setRoleCompanyGroupLoadError(null)
    ;(async () => {
      try {
        const list = await getCompanyGroupAdminList()
        if (cancelled) return
        const opts = list
          .filter((row) => row.isActive !== false && row.active !== false)
          .map((row) => {
            const id = Number(row.id)
            const label = (row.name || row.companyGroupAdminDetails || `Company group #${id}`).trim()
            return {id, label}
          })
          .filter((o) => Number.isFinite(o.id))
        opts.sort((a, b) => a.label.localeCompare(b.label))
        setRoleCompanyGroupOptions(opts)
      } catch (e: any) {
        if (!cancelled) {
          setRoleCompanyGroupOptions([])
          setRoleCompanyGroupLoadError(e?.message || 'Could not load company groups.')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [roleCrudOpen, isSuperUser])

  const scopedCompanyGroupLabel = useMemo(() => {
    if (actorCgaId == null) return null
    const hit = roleCompanyGroupOptions.find((o) => o.id === actorCgaId)
    if (hit) return `${hit.label} (company group #${actorCgaId})`
    return `Company group #${actorCgaId}`
  }, [actorCgaId, roleCompanyGroupOptions])

  const roleCrudRequiresCompanyGroup =
    roleCrudMode === 'create' || roleCrudMode === 'duplicate'
  const roleCrudScopedWithoutCompany =
    roleCrudRequiresCompanyGroup && !isSuperUser && actorCgaId == null

  const openRoleCrud = (mode: RoleCrudMode, role?: RoleRecord | null) => {
    const target = role ?? null
    setRoleCrudMode(mode)
    setRoleCrudTarget(target)
    if (mode === 'create') {
      setRoleNameInput('')
      setRoleCodeInput('')
      setRoleDescriptionInput('')
      setRoleBaseTypeInput('COMPANY_ADMIN')
      setRoleActiveInput(true)
      setRoleCompanyGroupAdminId(isSuperUser ? null : actorCgaId)
    } else {
      setRoleNameInput((target?.displayName || target?.roleType || '').trim())
      setRoleCodeInput((target?.code || '').trim())
      setRoleDescriptionInput((target?.description || '').trim())
      setRoleBaseTypeInput((target?.baseRoleType || 'COMPANY_ADMIN').toString())
      setRoleActiveInput(target?.active !== false)
      setRoleCompanyGroupAdminId(
        target?.companyGroupAdminId != null ? Number(target.companyGroupAdminId) : isSuperUser ? null : actorCgaId
      )
    }
    setRoleCrudOpen(true)
  }

  const refreshRoleData = async (focusRoleId?: number | null) => {
    const cgaFilter = isSuperUser ? undefined : actorCgaId
    const [roles, rps, users] = await Promise.all([
      getRolesList(false, cgaFilter ?? null),
      getRolePermissionsList(),
      getManagementUsers().catch(() => [] as ManagementUser[]),
    ])
    const visible = isSuperUser ? roles : roles.filter((r) => !isSuperAdminRoleRecord(r))
    setRolesList(visible)
    setRolePermissionsList(rps)
    setManagementUsers(users || [])
    if (focusRoleId != null && Number.isFinite(Number(focusRoleId))) {
      setSelectedRoleId(Number(focusRoleId))
    } else if (visible.length > 0 && selectedRoleId != null && !visible.some((r) => r.id === selectedRoleId)) {
      setSelectedRoleId(visible[0].id)
    }
  }

  const handleSaveRoleCrud = async () => {
    if (roleCrudMode !== 'view' && !roleNameInput.trim()) {
      toast.error('Role name is required.')
      return
    }
    if (roleCrudScopedWithoutCompany) {
      toast.error('You do not have a company scope to create custom roles.')
      return
    }
    setRoleCrudBusy(true)
    try {
      if (roleCrudMode === 'create') {
        const created = await createCustomRole({
          displayName: roleNameInput.trim(),
          code: roleCodeInput.trim() || null,
          baseRoleType: roleBaseTypeInput,
          description: roleDescriptionInput.trim() || null,
          active: roleActiveInput,
          ...(isSuperUser ? {companyGroupAdminId: roleCompanyGroupAdminId} : {}),
        })
        await refreshRoleData(created.id)
        toast.success('Role created.')
      } else if (roleCrudMode === 'edit' && roleCrudTarget?.id) {
        await updateRole(roleCrudTarget.id, {
          displayName: roleNameInput.trim(),
          description: roleDescriptionInput.trim() || null,
          active: roleActiveInput,
          code: roleCodeInput.trim() || null,
          baseRoleType: roleBaseTypeInput,
        })
        await refreshRoleData(roleCrudTarget.id)
        toast.success('Role updated.')
      } else if (roleCrudMode === 'duplicate' && roleCrudTarget?.id) {
        const duplicated = await createCustomRole({
          displayName: roleNameInput.trim(),
          code: roleCodeInput.trim() || null,
          baseRoleType: roleBaseTypeInput,
          description: roleDescriptionInput.trim() || null,
          active: roleActiveInput,
          copyFromRoleId: roleCrudTarget.id,
          ...(isSuperUser ? {companyGroupAdminId: roleCompanyGroupAdminId} : {}),
        })
        await refreshRoleData(duplicated.id)
        toast.success('Role duplicated.')
      }
      setRoleCrudOpen(false)
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save role')
    } finally {
      setRoleCrudBusy(false)
    }
  }

  const actionManagePermissions = (role: RoleRecord) => {
    setSelectedRoleId(role.id)
    setActiveTab('roles')
  }

  const rowTriState = (row: MatrixRow): ModuleTriState => {
    const codes = grantableCodesFor(allCodesOnRow(row))
    if (codes.length === 0) return 'none'
    let on = 0
    for (const code of codes) {
      if (draftCodes.has(code)) on++
    }
    if (on === 0) return 'none'
    if (on === codes.length) return 'all'
    return 'some'
  }

  if (!open) return null

  const roleTitleName =
    (selectedRole?.displayName || selectedRole?.roleType || 'Role').replace(/_/g, ' ').trim() || 'Role'
  const roleTabActive = activeTab === 'roles'
  const modalTitle =
    activeTab === 'role-management'
      ? 'Role management'
      : activeTab === 'roles'
      ? `Assign Permissions For ${roleTitleName}`
      : activeTab === 'operator-sub-roles'
        ? 'Operator sub-roles'
        : 'Crew rank permissions'
  const activeTabHelperText =
    activeTab === 'role-management'
      ? 'Create roles and manage their permissions.'
      : activeTab === 'roles'
      ? 'Select a role and manage its permissions.'
      : activeTab === 'operator-sub-roles'
        ? 'Operator sub-roles define default access for operator types.'
        : 'Crew rank permissions define default access for crew ranks.'

  return (
    <>
      <style>{`
        .role-perm-matrix-modal {
          overflow: hidden;
        }
        .role-perm-matrix-modal .modal-dialog {
          width: min(96vw, 1600px);
          max-width: 96vw;
          margin: 0.75rem auto;
          max-height: 92vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .role-perm-matrix-modal .modal-content {
          width: 100%;
          max-height: 92vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .role-perm-matrix-modal .rpm-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          overflow: hidden;
        }
        .role-perm-matrix-modal .rpm-header-main {
          flex: 1 1 520px;
          min-width: 280px;
          min-inline-size: 280px;
          overflow: hidden;
        }
        .role-perm-matrix-modal .rpm-tabs-container {
          display: inline-flex;
          align-items: center;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 4px;
          gap: 4px;
          margin-bottom: 0.85rem;
          max-width: 100%;
          overflow-x: auto;
        }
        .role-perm-matrix-modal .rpm-tab-btn {
          border: none;
          background: transparent;
          color: #475569;
          font-family: inherit;
          font-weight: 600;
          font-size: 0.8125rem;
          line-height: 1.25;
          padding: 7px 15px;
          border-radius: 7px;
          transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
          white-space: nowrap;
          cursor: pointer;
          user-select: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .role-perm-matrix-modal .rpm-tab-btn:hover:not(.active) {
          color: #0f172a;
          background: rgba(255, 255, 255, 0.7);
        }
        .role-perm-matrix-modal .rpm-tab-btn.active {
          background: #2563eb;
          color: #ffffff;
          box-shadow: 0 2px 6px rgba(37, 99, 235, 0.28), 0 1px 2px rgba(37, 99, 235, 0.16);
          font-weight: 700;
        }
        .role-perm-matrix-modal .rpm-tab-btn:active {
          transform: scale(0.98);
        }
        .role-perm-matrix-modal .rpm-tab-btn:focus-visible {
          outline: 2px solid #2563eb;
          outline-offset: 2px;
        }
        [data-theme='dark'] .role-perm-matrix-modal .rpm-tabs-container,
        .theme-dark .role-perm-matrix-modal .rpm-tabs-container {
          background: #1e293b;
          border-color: #334155;
        }
        [data-theme='dark'] .role-perm-matrix-modal .rpm-tab-btn,
        .theme-dark .role-perm-matrix-modal .rpm-tab-btn {
          color: #94a3b8;
        }
        [data-theme='dark'] .role-perm-matrix-modal .rpm-tab-btn:hover:not(.active),
        .theme-dark .role-perm-matrix-modal .rpm-tab-btn:hover:not(.active) {
          color: #f8fafc;
          background: rgba(255, 255, 255, 0.08);
        }
        [data-theme='dark'] .role-perm-matrix-modal .rpm-tab-btn.active,
        .theme-dark .role-perm-matrix-modal .rpm-tab-btn.active {
          background: #3b82f6;
          color: #ffffff;
        }
        .role-perm-matrix-modal .rpm-header-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
          margin-left: auto;
          flex: 1 1 420px;
          justify-content: flex-end;
        }
        .role-perm-matrix-modal .rpm-search {
          flex: 1 1 320px;
          min-width: 260px;
          max-width: 420px;
        }
        .role-perm-matrix-modal .rpm-close-btn {
          flex: 0 0 auto;
          margin-left: auto;
        }
        .role-perm-matrix-modal .rpm-body {
          overflow: hidden;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .role-perm-matrix-modal .rpm-content-scroll {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
        }
        .role-perm-matrix-modal .rpm-advanced-content {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          display: flex;
          flex-direction: column;
        }
        .role-perm-matrix-modal .rpm-crew-rank-shell {
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          padding: 1rem 1rem 0;
        }
        .role-perm-matrix-modal .rpm-crew-rank-shell .crew-rank-permissions-panel {
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .role-perm-matrix-modal .permission-matrix-panel {
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .role-perm-matrix-modal .permission-matrix-table-scroll {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          overflow-x: auto;
          display: flex;
          flex-direction: column;
        }
        .role-perm-matrix-modal .rpm-table-scroll {
          border: 1px solid ${TABLE_BORDER_OUTER};
          border-radius: 6px;
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          overflow-y: visible;
          background: #fff;
        }
        .role-perm-matrix-modal .permission-matrix-table-scroll > .rpm-table-scroll {
          flex: 1 1 auto;
          min-height: 0;
        }
        .role-perm-matrix-modal .table-matrix-perm {
          border-collapse: separate;
          border-spacing: 0;
          border: 1px solid ${TABLE_CELL_BORDER};
          width: 100%;
          min-width: 1200px;
        }
        .role-perm-matrix-modal .table-matrix-perm th {
          font-weight: 700;
          font-size: 0.72rem;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          border-color: ${TABLE_CELL_BORDER} !important;
          border-bottom: 2px solid rgba(0,0,0,0.25) !important;
          color: #fff;
          padding: 0.55rem 0.5rem;
          white-space: nowrap;
          min-height: 44px;
          vertical-align: middle;
        }
        .role-perm-matrix-modal .table-matrix-perm td {
          font-size: 0.8rem;
          border-color: ${TABLE_CELL_BORDER} !important;
          padding: 0.5rem 0.45rem;
          border-bottom: 1px solid ${TABLE_CELL_BORDER};
          vertical-align: top;
        }
        .role-perm-matrix-modal .table-matrix-perm tbody td.rpm-td-squeeze {
          vertical-align: top;
          white-space: normal;
        }
        .role-perm-matrix-modal .table-matrix-perm th + th,
        .role-perm-matrix-modal .table-matrix-perm td + td {
          border-left: 1px solid ${TABLE_CELL_BORDER};
        }
        .role-perm-matrix-modal .rpm-module-cell {
          background: ${MODULE_CELL_BG} !important;
          font-weight: 700;
          vertical-align: top;
        }
        .role-perm-matrix-modal .rpm-module-cell .rpm-module-title {
          font-weight: 700;
          color: #1a1a2e;
        }
        .role-perm-matrix-modal .table-matrix-perm tbody tr:nth-child(even) td:not(.rpm-module-cell) {
          background: ${ROW_ALT};
        }
        .role-perm-matrix-modal .table-matrix-perm tbody tr:nth-child(odd) td:not(.rpm-module-cell) {
          background: #fff;
        }
        .role-perm-matrix-modal .rpm-perm-cell {
          min-width: 620px;
          vertical-align: top;
          padding-left: 36px !important;
          padding-right: 12px !important;
        }
        .role-perm-matrix-modal .rpm-perm-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 8px 20px;
          align-items: center;
          justify-content: flex-start;
          white-space: normal;
          overflow: visible;
          margin-left: 0 !important;
          padding-left: 0 !important;
          transform: none;
          position: static;
        }
        .role-perm-matrix-modal .rpm-perm-item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin: 0;
          padding: 1px 0;
          flex: 0 0 auto;
          min-width: 90px;
          width: auto;
          max-width: none;
          white-space: nowrap;
        }
        .role-perm-matrix-modal .rpm-perm-item.form-check {
          padding-left: 0;
          min-height: 0;
        }
        .role-perm-matrix-modal .rpm-perm-item .perm-matrix-check {
          margin-top: 0.15rem;
          flex-shrink: 0;
        }
        .role-perm-matrix-modal .rpm-perm-item label {
          display: inline;
          margin: 0;
          white-space: nowrap;
          overflow: visible;
          text-overflow: unset;
          line-height: 1.25;
          cursor: pointer;
          min-width: auto;
          max-width: none;
          font-size: 12px;
        }
        .role-perm-matrix-modal .perm-matrix-check {
          width: 0.95rem;
          height: 0.95rem;
          flex-shrink: 0;
        }
        .role-perm-matrix-modal .perm-matrix-check:checked {
          background-color: ${CHECK_ACCENT};
          border-color: ${CHECK_ACCENT};
        }
        .role-perm-matrix-modal .perm-matrix-check:indeterminate {
          background-color: ${CHECK_ACCENT};
          border-color: ${CHECK_ACCENT};
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10h8'/%3e%3c/svg%3e");
        }
        .role-perm-matrix-modal .perm-matrix-check:focus {
          box-shadow: 0 0 0 0.2rem rgba(11, 183, 160, 0.25);
        }
        .role-perm-matrix-modal .rpm-bulk-bar .btn {
          font-size: 0.8rem;
          font-weight: 500;
          border-color: #e4e6ef;
        }
        .role-perm-matrix-modal .rpm-bulk-bar {
          overflow: hidden;
        }
        .role-perm-matrix-modal .modal-footer-sticky {
          flex-shrink: 0;
          background: #fff;
          z-index: 5;
          overflow-x: hidden;
          box-shadow: 0 -6px 16px rgba(0,0,0,0.07);
        }
      `}</style>
      <div
        className='modal fade show d-block role-perm-matrix-modal'
        role='dialog'
        aria-modal
        style={{backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1080}}
      >
        <div className='modal-dialog modal-dialog-centered px-2 d-flex mb-0'>
          <div
            className='modal-content d-flex flex-column flex-grow-1 shadow'
            style={{border: `1px solid ${TABLE_BORDER_OUTER}`}}
          >
            <div
              className='modal-header rpm-header border-bottom flex-shrink-0 py-3'
              style={{paddingLeft: '1.15rem', paddingRight: '1.15rem'}}
            >
              <div className='rpm-header-main min-w-0 pe-2'>
                <h4 className='modal-title fw-bold text-gray-900 mb-2' style={{fontSize: '1.1rem', lineHeight: 1.3}}>
                  {modalTitle}
                </h4>
                <div className='text-muted small mb-2'>{activeTabHelperText}</div>
                <div className='rpm-tabs-container mb-3' role='tablist' aria-label='Roles and permissions sections'>
                  {availableTabs.includes('role-management') && (
                    <button
                      type='button'
                      className={`rpm-tab-btn ${activeTab === 'role-management' ? 'active' : ''}`}
                      onClick={() => setActiveTab('role-management')}
                    >
                      <svg
                        width='14'
                        height='14'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        aria-hidden='true'
                      >
                        <path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' />
                        <circle cx='9' cy='7' r='4' />
                        <path d='M23 21v-2a4 4 0 0 0-3-3.87' />
                        <path d='M16 3.13a4 4 0 0 1 0 7.75' />
                      </svg>
                      Role management
                    </button>
                  )}
                  {availableTabs.includes('roles') && (
                    <button
                      type='button'
                      className={`rpm-tab-btn ${activeTab === 'roles' ? 'active' : ''}`}
                      onClick={() => setActiveTab('roles')}
                    >
                      <svg
                        width='14'
                        height='14'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        aria-hidden='true'
                      >
                        <path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
                        <path d='m9 12 2 2 4-4' />
                      </svg>
                      Role permissions
                    </button>
                  )}
                  {availableTabs.includes('operator-sub-roles') && (
                    <button
                      type='button'
                      className={`rpm-tab-btn ${
                        activeTab === 'operator-sub-roles' ? 'active' : ''
                      }`}
                      onClick={() => setActiveTab('operator-sub-roles')}
                    >
                      <svg
                        width='14'
                        height='14'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        aria-hidden='true'
                      >
                        <polygon points='12 2 2 7 12 12 22 7 12 2' />
                        <polyline points='2 17 12 22 22 17' />
                        <polyline points='2 12 12 17 22 12' />
                      </svg>
                      Operator sub-roles
                    </button>
                  )}
                  {availableTabs.includes('crew-rank-permissions') && (
                    <button
                      type='button'
                      className={`rpm-tab-btn ${
                        activeTab === 'crew-rank-permissions' ? 'active' : ''
                      }`}
                      onClick={() => setActiveTab('crew-rank-permissions')}
                    >
                      <svg
                        width='14'
                        height='14'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        aria-hidden='true'
                      >
                        <circle cx='12' cy='8' r='7' />
                        <polyline points='8.21 13.89 7 23 12 20 17 23 15.79 13.88' />
                      </svg>
                      Crew rank permissions
                    </button>
                  )}
                </div>
                {roleTabActive ? (
                  <div className='d-flex flex-wrap align-items-center gap-2'>
                    <label className='visually-hidden' htmlFor='role-perm-modal-role-select'>
                      Role
                    </label>
                    <Select<RoleSelectOption, false>
                      inputId='role-perm-modal-role-select'
                      className='rpm-role-select'
                      classNamePrefix='rpm-role-select'
                      styles={roleSelectStyles}
                      options={roleSelectOptions}
                      value={roleSelectOptions.find((option) => option.value === selectedRoleId) ?? null}
                      isDisabled={bootLoading || roleSelectorRoles.length === 0}
                      onChange={(option) => setSelectedRoleId(option?.value ?? null)}
                      isSearchable={false}
                      isClearable={false}
                      menuPlacement='auto'
                      menuPosition='fixed'
                      menuPortalTarget={document.body}
                      aria-label='Select role'
                      placeholder={roleSelectorRoles.length === 0 ? 'No roles' : 'Select role'}
                    />
                    {selectedRole?.systemRole ? (
                      <span className='badge badge-light-primary text-gray-800'>System</span>
                    ) : null}
                    {selectedRole?.customRole ? (
                      <span className='badge badge-light-info text-gray-800'>Custom</span>
                    ) : null}
                    {readOnlyNotice ? (
                      <span
                        className='badge badge-light-dark text-gray-800 text-wrap text-start'
                        style={{maxWidth: '100%', fontWeight: 500}}
                      >
                        {readOnlyNotice}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className='rpm-header-actions'>
                {roleTabActive ? (
                  <>
                    <input
                      type='search'
                      className='form-control form-control-sm rpm-search'
                      placeholder='Search modules, menus, submenus…'
                      value={permSearch}
                      onChange={(e) => setPermSearch(e.target.value)}
                      aria-label='Search permissions table'
                    />
                  </>
                ) : null}
                <button
                  type='button'
                  className='btn btn-icon btn-sm btn-light rpm-close-btn'
                  aria-label='Close'
                  onClick={handleCancel}
                >
                  <span className='fs-3 lh-1' aria-hidden>
                    ×
                  </span>
                </button>
              </div>
            </div>

            {roleTabActive ? (
              <div className='rpm-bulk-bar border-bottom px-3 py-2 d-flex flex-wrap align-items-center gap-1 flex-shrink-0 bg-light'>
                <span className='small text-muted me-2'>Bulk actions</span>
                <button
                  type='button'
                  className='btn btn-sm btn-light py-1 px-2'
                  disabled={readOnlyPermissions || bootLoading || matrixRows.length === 0}
                  onClick={selectAllCatalog}
                >
                  Select all
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-light py-1 px-2'
                  disabled={readOnlyPermissions || bootLoading || matrixRows.length === 0}
                  onClick={clearAllCatalog}
                >
                  Unselect all
                </button>
                <span className='text-muted px-1'>·</span>
                <button
                  type='button'
                  className='btn btn-sm btn-light py-1 px-2'
                  disabled={readOnlyPermissions || bootLoading || filteredRows.length === 0 || !searchActive}
                  onClick={selectVisibleOnly}
                  title={!searchActive ? 'Use search to narrow rows first' : undefined}
                >
                  Select visible
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-light py-1 px-2'
                  disabled={readOnlyPermissions || bootLoading || filteredRows.length === 0 || !searchActive}
                  onClick={clearVisibleOnly}
                >
                  Unselect visible
                </button>
                <span className='small text-muted ms-md-2'>
                  {searchActive ? 'Applies to filtered rows only.' : 'Select all / Unselect all use the full catalog.'}
                </span>
              </div>
            ) : null}

            <div className='modal-body rpm-body p-0 flex-grow-1'>
              {!roleTabActive ? (
                activeTab === 'crew-rank-permissions' ? (
                  <div className='rpm-crew-rank-shell'>
                    <CrewRankPermissionsManagerPanel
                      canEdit={gates.canEditCrewRankPermissions}
                      actorIsSuperAdmin={isSuperUser}
                    />
                  </div>
                ) : (
                  <div className={`p-4${activeTab === 'role-management' ? ' rpm-content-scroll' : ''}`}>
                    {activeTab === 'role-management' ? (
                    <div className='border rounded p-3 bg-white'>
                      <div className='d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3'>
                        <div>
                          <h5 className='fw-bold text-gray-900 mb-1'>Role management</h5>
                          <p className='text-muted small mb-0'>Create roles and manage their permissions.</p>
                        </div>
                        <button
                          type='button'
                          className='btn btn-primary btn-sm'
                          disabled={!gates.canCreateRole}
                          onClick={() => openRoleCrud('create')}
                        >
                          + Create role
                        </button>
                      </div>
                      {!gates.canReadRole ? (
                        <div className='alert alert-warning py-2 px-3 small mb-3'>
                          Access denied — you do not have permission to view roles.
                        </div>
                      ) : rolesAccessDenied ? (
                        <div className='alert alert-warning py-2 px-3 small mb-3'>
                          Access denied — you do not have permission to view roles.
                        </div>
                      ) : null}
                      <div className='table-responsive'>
                        <table className='table table-sm table-row-dashed align-middle mb-0'>
                          <thead>
                            <tr className='text-muted small text-uppercase'>
                              <th>Role name</th>
                              <th>Type / behavior</th>
                              <th>Company scope</th>
                              <th>Status</th>
                              <th>Permissions</th>
                              <th>Users</th>
                              <th className='text-end'>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!gates.canReadRole || rolesAccessDenied ? (
                              <tr>
                                <td colSpan={7} className='text-center text-muted py-3'>
                                  Access denied — you do not have permission to view roles.
                                </td>
                              </tr>
                            ) : rolesList.length === 0 ? (
                              <tr>
                                <td colSpan={7} className='text-center text-muted py-3'>
                                  No roles found.
                                </td>
                              </tr>
                            ) : (
                              rolesList.map((role) => {
                                const scopeLabel =
                                  role.companyGroupAdminId == null
                                    ? 'Global'
                                    : role.companyGroupName?.trim() ||
                                      `Company Group #${role.companyGroupAdminId}`
                                const permissionCount = permissionCountByRoleId.get(role.id) ?? 0
                                const usersCount = usersCountByRoleId.get(role.id) ?? 0
                                const roleLabel = (role.displayName || role.roleType || `Role ${role.id}`).replace(
                                  /_/g,
                                  ' '
                                )
                                return (
                                  <tr key={role.id}>
                                    <td className='fw-semibold text-gray-900'>{roleLabel}</td>
                                    <td className='small text-gray-700'>
                                      {(role.roleType || '').replace(/_/g, ' ')}
                                      {role.baseRoleType ? ` / ${String(role.baseRoleType).replace(/_/g, ' ')}` : ''}
                                    </td>
                                    <td className='small text-gray-700'>{scopeLabel}</td>
                                    <td>
                                      <span className={`badge ${role.active === false ? 'badge-light-danger' : 'badge-light-success'}`}>
                                        {role.active === false ? 'Inactive' : 'Active'}
                                      </span>
                                    </td>
                                    <td>{permissionCount}</td>
                                    <td>{usersCount}</td>
                                    <td className='text-end'>
                                      <div className='d-flex gap-1 justify-content-end flex-wrap'>
                                        <button
                                          type='button'
                                          className='btn btn-sm btn-light'
                                          disabled={!gates.canUpdateRole}
                                          onClick={() => openRoleCrud('edit', role)}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type='button'
                                          className='btn btn-sm btn-light-primary'
                                          onClick={() => actionManagePermissions(role)}
                                        >
                                          Manage permissions
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                )
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <OperatorSubRolesManagerPanel />
                  )}
                  </div>
                )
              ) : bootLoading ? (
                <div className='p-4 text-center text-muted small'>Loading roles and permission catalog…</div>
              ) : catalogAccessDenied || rolePermissionsAccessDenied ? (
                <div className='p-4 text-center text-muted small'>
                  Access denied — you do not have permission to view role permissions.
                </div>
              ) : rolesAccessDenied ? (
                <div className='p-4 text-center text-muted small'>
                  Access denied — you do not have permission to view roles.
                </div>
              ) : editableRoles.length === 0 ? (
                <div className='p-4 text-center text-muted small'>
                  No editable lower roles are available in your company group.
                </div>
              ) : (
                <div className='rpm-advanced-content'>
                  <div className='small text-muted mt-2 mb-2 px-4'>
                    Menu-level matrix: expand modules and tick permissions to build the role. Use search to narrow rows.
                  </div>
                  <div className='rpm-table-scroll mx-3 mb-2'>
                    <table className='table table-matrix-perm mb-0' style={{tableLayout: 'fixed'}}>
                      <colgroup>
                        <col style={{width: 44}} />
                        <col style={{width: 44}} />
                        <col style={{width: 140}} />
                        <col style={{width: 190}} />
                        <col style={{width: 220}} />
                        <col style={{width: 620}} />
                      </colgroup>
                      <thead style={{position: 'sticky', top: 0, zIndex: 3}}>
                        <tr style={{background: TABLE_HEADER_BG}}>
                          <th style={{background: TABLE_HEADER_BG}}>SR</th>
                          <th style={{background: TABLE_HEADER_BG}} className='text-center'>
                            <span className='visually-hidden'>Row</span>
                          </th>
                          <th style={{background: TABLE_HEADER_BG}}>Module</th>
                          <th style={{background: TABLE_HEADER_BG}}>Menu</th>
                          <th style={{background: TABLE_HEADER_BG}}>Submenu</th>
                          <th style={{background: TABLE_HEADER_BG}}>Permission</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.length === 0 ? (
                          <tr>
                            <td colSpan={6} className='text-center text-muted py-4 small'>
                              {matrixRows.length === 0
                                ? 'No permissions in catalog.'
                                : 'No rows match your search.'}
                            </td>
                          </tr>
                        ) : (
                          filteredRows.map((row, idx) => {
                            const rs = rowTriState(row)
                            const showModuleBulk =
                              idx === 0 || filteredRows[idx - 1].moduleName !== row.moduleName
                            return (
                              <tr key={row.rowKey}>
                                <td className='rpm-td-squeeze text-muted text-center small'>{idx + 1}</td>
                                <td className='rpm-td-squeeze text-center'>
                                  <input
                                    type='checkbox'
                                    className='form-check-input perm-matrix-check mt-1'
                                    aria-label={`Select row ${row.menuName} / ${row.subMenuName}`}
                                    disabled={readOnlyPermissions || grantableCodesFor(allCodesOnRow(row)).length === 0}
                                    checked={rs === 'all'}
                                    ref={(el) => {
                                      if (el) el.indeterminate = rs === 'some'
                                    }}
                                    onChange={() => toggleRowAll(row, rs !== 'all')}
                                  />
                                </td>
                                <td className='rpm-module-cell'>
                                  <div className='d-flex align-items-start gap-2 pt-1'>
                                    {showModuleBulk ? (
                                      <input
                                        type='checkbox'
                                        className='form-check-input perm-matrix-check flex-shrink-0'
                                        aria-label={`Select all permissions in ${row.moduleName}`}
                                        disabled={
                                          readOnlyPermissions ||
                                          grantableCodesFor(allCodesForModule(matrixRows, row.moduleName)).length === 0
                                        }
                                        ref={(el) => {
                                          moduleCheckRefs.current.set(row.moduleName, el)
                                          if (el) {
                                            const st = moduleTriStateForGrantable(row.moduleName)
                                            el.indeterminate = st === 'some'
                                            el.checked = st === 'all'
                                          }
                                        }}
                                        onChange={(e) => toggleModuleAll(row.moduleName, e.target.checked)}
                                      />
                                    ) : (
                                      <span className='flex-shrink-0' style={{width: '1.25rem'}} aria-hidden />
                                    )}
                                    <span className='rpm-module-title'>{row.moduleName}</span>
                                  </div>
                                </td>
                                <td className='rpm-td-squeeze'>{row.menuName}</td>
                                <td className='rpm-td-squeeze'>{row.subMenuName}</td>
                                <td className='rpm-perm-cell'>
                                  <div className='rpm-perm-grid'>
                                    {STD_ORDER.filter((k) => row.standard[k]).map((k) => {
                                      const code = row.standard[k]!
                                      const id = `rpm-${selectedRoleId}-${normalizeCode(code)}-${k}`
                                      return (
                                        <div key={code} className='rpm-perm-item form-check form-check-sm mb-0'>
                                          <input
                                            id={id}
                                            type='checkbox'
                                            className='form-check-input perm-matrix-check'
                                            checked={draftCodes.has(canonicalizeRolePermissionCode(code))}
                                            disabled={readOnlyPermissions || !canGrantPermission(code)}
                                            onChange={() => toggleDraftPermission(code)}
                                          />
                                          <label
                                            className='form-check-label small text-gray-800 mb-0'
                                            htmlFor={id}
                                            title={
                                              !canGrantPermission(code)
                                                ? grantDeniedReasonFor(code, dedupedPermissionCatalog)
                                                : [code, row.standardHints[k]].filter(Boolean).join('\n')
                                            }
                                          >
                                            <span className='fw-semibold text-gray-900'>{STD_LABELS[k]}</span>
                                            {row.standardHints[k] ? (
                                              <div
                                                className='text-muted small text-break lh-sm mt-1'
                                                style={{maxWidth: 280}}
                                              >
                                                {row.standardHints[k]}
                                              </div>
                                            ) : null}
                                          </label>
                                        </div>
                                      )
                                    })}
                                    {row.extraChecks.map((ex) => {
                                      const id = `rpm-ex-${selectedRoleId}-${normalizeCode(ex.code)}`
                                      return (
                                        <div key={ex.code} className='rpm-perm-item form-check form-check-sm mb-0'>
                                          <input
                                            id={id}
                                            type='checkbox'
                                            className='form-check-input perm-matrix-check'
                                            checked={draftCodes.has(canonicalizeRolePermissionCode(ex.code))}
                                            disabled={readOnlyPermissions || !canGrantPermission(ex.code)}
                                            onChange={() => toggleDraftPermission(ex.code)}
                                          />
                                          <label
                                            className='form-check-label small text-gray-800 mb-0'
                                            htmlFor={id}
                                            title={
                                              !canGrantPermission(ex.code)
                                                ? grantDeniedReasonFor(ex.code, dedupedPermissionCatalog)
                                                : [ex.code, ex.catalogDetail].filter(Boolean).join('\n')
                                            }
                                          >
                                            <span className='fw-semibold text-gray-900'>{ex.label}</span>
                                            {ex.catalogDetail ? (
                                              <div
                                                className='text-muted small text-break lh-sm mt-1'
                                                style={{maxWidth: 280}}
                                              >
                                                {ex.catalogDetail}
                                              </div>
                                            ) : null}
                                          </label>
                                        </div>
                                      )
                                    })}
                                  </div>
                                  {STD_ORDER.every((k) => !row.standard[k]) && row.extraChecks.length === 0 ? (
                                    <span className='text-muted small'>—</span>
                                  ) : null}
                                </td>
                              </tr>
                            )
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {roleTabActive ? (
              <div className='modal-footer border-top modal-footer-sticky d-flex justify-content-between flex-wrap gap-2 flex-shrink-0 py-2'>
                <span className='small text-muted align-self-center'>
                  {dirtyPermissions && !readOnlyPermissions ? 'Unsaved permission changes.' : '\u00a0'}
                </span>
                <div className='d-flex gap-2'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light py-1 px-2'
                    disabled={saving}
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-primary'
                    style={{minWidth: '132px'}}
                    disabled={saving || selectedRoleId == null || readOnlyPermissions || !dirtyPermissions}
                    onClick={handleSavePermissions}
                  >
                    {saving ? 'Saving…' : 'Save Permissions'}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {roleCrudOpen ? (
        <div className='modal fade show d-block' role='dialog' aria-modal style={{backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1095}}>
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content admin-access-role-catalog-modal'>
              <div className='modal-header border-bottom'>
                <h5 className='modal-title text-gray-900 fw-bold'>
                  {roleCrudMode === 'create'
                    ? 'Create role'
                    : roleCrudMode === 'edit'
                      ? 'Edit role'
                      : roleCrudMode === 'duplicate'
                        ? 'Duplicate role'
                        : 'View role'}
                </h5>
                <button
                  type='button'
                  className='btn-close'
                  aria-label='Close'
                  onClick={() => !roleCrudBusy && setRoleCrudOpen(false)}
                />
              </div>
              <div className='modal-body'>
                <div className='mb-3'>
                  <label className='form-label fw-semibold'>Role name</label>
                  <input
                    className='form-control'
                    aria-label='Role name'
                    value={roleNameInput}
                    onChange={(e) => setRoleNameInput(e.target.value)}
                    disabled={roleCrudMode === 'view'}
                  />
                </div>
                {roleCrudRequiresCompanyGroup ? (
                  isSuperUser ? (
                    <div className='mb-3'>
                      <label className='form-label fw-semibold'>Company group (optional)</label>
                      <select
                        className='form-select'
                        value={roleCompanyGroupAdminId == null ? '' : String(roleCompanyGroupAdminId)}
                        onChange={(e) => {
                          const v = e.target.value
                          setRoleCompanyGroupAdminId(v === '' ? null : Number(v))
                        }}
                        disabled={(roleCrudMode as string) === 'view'}
                      >
                        <option value=''>Select company group…</option>
                        {roleCompanyGroupOptions.map((o) => (
                          <option key={o.id} value={String(o.id)}>
                            {o.label} (company group #{o.id})
                          </option>
                        ))}
                      </select>
                      <p className='small text-muted mb-0 mt-1'>
                        Leave blank to create a global custom role. Select a company group to scope the role to that company only.
                      </p>
                      {roleCompanyGroupLoadError ? (
                        <p className='small text-danger mb-0 mt-1'>{roleCompanyGroupLoadError}</p>
                      ) : null}
                    </div>
                  ) : (
                    <div className='mb-3'>
                      <label className='form-label fw-semibold'>Company group</label>
                      <div className='form-control-plaintext border rounded px-3 py-2 bg-light small text-gray-800'>
                        {scopedCompanyGroupLabel ?? 'Your organization (scoped automatically)'}
                      </div>
                    </div>
                  )
                ) : roleCrudTarget?.companyGroupAdminId != null ? (
                  <div className='mb-3'>
                    <label className='form-label fw-semibold'>Company group</label>
                    <div className='form-control-plaintext border rounded px-3 py-2 bg-light small text-gray-800'>
                      Company group #{roleCrudTarget.companyGroupAdminId}
                    </div>
                  </div>
                ) : null}
                <div className='mb-3'>
                  <label className='form-label fw-semibold'>Role code (optional)</label>
                  <input
                    className='form-control'
                    value={roleCodeInput}
                    onChange={(e) => setRoleCodeInput(e.target.value)}
                    disabled={roleCrudMode === 'view'}
                  />
                </div>
                <div className='mb-3'>
                  <label className='form-label fw-semibold'>Base behavior</label>
                  <select
                    className='form-select'
                    value={roleBaseTypeInput}
                    onChange={(e) => setRoleBaseTypeInput(e.target.value)}
                    disabled={roleCrudMode === 'view'}
                  >
                    {['COMPANY_GROUP_ADMIN', 'COMPANY_ADMIN', 'SHORE', 'OPERATOR', 'CREW', 'VENDOR'].map((v) => (
                      <option key={v} value={v}>
                        {v.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='mb-3'>
                  <label className='form-label fw-semibold'>Description</label>
                  <textarea
                    className='form-control'
                    rows={3}
                    value={roleDescriptionInput}
                    onChange={(e) => setRoleDescriptionInput(e.target.value)}
                    disabled={roleCrudMode === 'view'}
                  />
                </div>
                <div className='form-check'>
                  <input
                    id='role-crud-active'
                    className='form-check-input'
                    type='checkbox'
                    checked={roleActiveInput}
                    onChange={(e) => setRoleActiveInput(e.target.checked)}
                    disabled={roleCrudMode === 'view'}
                  />
                  <label className='form-check-label' htmlFor='role-crud-active'>
                    Active
                  </label>
                </div>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-light' disabled={roleCrudBusy} onClick={() => setRoleCrudOpen(false)}>
                  {roleCrudMode === 'view' ? 'Close' : 'Cancel'}
                </button>
                {roleCrudMode !== 'view' ? (
                  <button
                    type='button'
                    className='btn btn-primary'
                    disabled={roleCrudBusy || roleCrudScopedWithoutCompany}
                    onClick={handleSaveRoleCrud}
                  >
                    {roleCrudBusy ? 'Saving…' : 'Save'}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
